import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { X, CalendarDays } from "lucide-react";

interface DateProposerProps {
  proposedDates: string[];
  onPropose: (dates: string[]) => void;
  disabled?: boolean;
  className?: string;
}

function formatDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDisplayDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", weekday: "short" });
}

const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export function DateProposer({ proposedDates, onPropose, disabled = false, className }: DateProposerProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const days = useMemo(() => {
    const today = new Date();
    const result: { date: Date; key: string }[] = [];
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      result.push({ date: d, key: formatDateString(d) });
    }
    return result;
  }, []);

  const firstDayOffset = days[0]?.date.getDay() ?? 0;

  const proposedSet = useMemo(() => new Set(proposedDates), [proposedDates]);

  const toggleDay = (key: string) => {
    if (disabled) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handlePropose = () => {
    const allDates = [...new Set([...proposedDates, ...selected])].sort();
    onPropose(allDates);
    setSelected(new Set());
  };

  const removeProposed = (dateStr: string) => {
    if (disabled) return;
    onPropose(proposedDates.filter((d) => d !== dateStr));
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Calendar header */}
      <div className="flex items-center gap-2 mb-1">
        <CalendarDays className="w-4 h-4 text-app-primary" />
        <span className="text-body font-semibold text-app-text">Select Dates</span>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {DAY_LABELS.map((label) => (
          <div key={label} className="text-info text-app-text-muted font-medium py-1">
            {label}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Offset spacers */}
        {Array.from({ length: firstDayOffset }).map((_, i) => (
          <div key={`spacer-${i}`} />
        ))}

        {days.map(({ date, key }) => {
          const isProposed = proposedSet.has(key);
          const isSelected = selected.has(key);
          const isToday = key === formatDateString(new Date());

          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => toggleDay(key)}
              className={cn(
                "h-9 rounded-xl text-sm font-medium",
                "transition-all duration-150 cursor-pointer",
                "hover:scale-105 active:scale-95",
                "disabled:cursor-not-allowed disabled:opacity-50",
                isSelected && "bg-app-accent text-white shadow-md ring-2 ring-app-accent/50",
                !isSelected && isProposed && "bg-app-primary/20 border border-app-primary text-app-primary font-bold",
                !isSelected && !isProposed && "bg-app-surface-raised text-app-text hover:bg-app-primary/10",
                isToday && !isSelected && !isProposed && "ring-2 ring-app-primary/30",
              )}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Propose button */}
      {selected.size > 0 && (
        <div className="animate-in fade-in-0 slide-in-from-bottom-2">
          <Button
            type="button"
            onClick={handlePropose}
            disabled={disabled}
            className="w-full bg-app-primary hover:bg-app-primary-hover rounded-xl"
          >
            Propose {selected.size} Date{selected.size > 1 ? "s" : ""}
          </Button>
        </div>
      )}

      {/* Proposed date chips */}
      {proposedDates.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-info text-app-text-muted font-medium">Proposed dates</p>
          <div className="flex flex-wrap gap-1.5">
            {proposedDates.map((dateStr) => (
              <span
                key={dateStr}
                className={cn(
                  "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm",
                  "bg-app-primary/15 text-app-primary font-medium",
                  "transition-all duration-150",
                )}
              >
                {formatDisplayDate(dateStr)}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeProposed(dateStr)}
                    className="hover:bg-app-primary/20 rounded-full p-0.5 cursor-pointer transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
